import { forwardRef } from "react";

const truncateFileName = (file, maxLength = 20) => {
  if (!file || !file.name) return file;
  const lastDot = file.name.lastIndexOf(".");
  let newName = file.name;
  if (lastDot > 0) {
    const ext = file.name.substring(lastDot);
    const base = file.name.substring(0, lastDot);
    if (base.length > maxLength) {
      const extWithoutDot = ext.startsWith(".") ? ext.slice(1) : ext;
      newName = `${base.substring(0, maxLength)}...${extWithoutDot}`;
    }
  } else {
    if (file.name.length > maxLength) {
      newName = `${file.name.substring(0, maxLength)}...`;
    }
  }
  if (newName === file.name) return file;
  try {
    return new File([file], newName, {
      type: file.type,
      lastModified: file.lastModified,
    });
  } catch (e) {
    return file;
  }
};

const FileInput = forwardRef(function FileInput(
  {
    label,
    handleFileChange,
    acceptFormat,
    required,
    id,
    name,
    imageURL,
    errorMessage,
    disabled,
    multiple = false,
    onClearImage,
    ariaLabel = "",
    maxFileNameLength = 20,
    className = "",
    labelClassName = "",
    labelStyle = {},
    "aria-label": ariaLabelProp,
    "aria-required": ariaRequiredProp,
  },
  ref
) {
  const isRequired =
    ariaRequiredProp !== undefined ? ariaRequiredProp : required;
  const ariaLabelText =
    ariaLabelProp ||
    ariaLabel ||
    (typeof label === "string" && label.trim() ? label.trim() : "") ||
    (typeof name === "string" && name.trim() ? name.trim() : "") ||
    "Choose file";
  const onInputChange = (e) => {
    if (e?.target?.files && e.target.files.length > 0) {
      try {
        let hasLongName = false;
        for (let i = 0; i < e.target.files.length; i++) {
          if (
            truncateFileName(e.target.files[i], maxFileNameLength) !==
            e.target.files[i]
          ) {
            hasLongName = true;
            break;
          }
        }
        if (hasLongName && typeof DataTransfer !== "undefined") {
          const dt = new DataTransfer();
          for (let i = 0; i < e.target.files.length; i++) {
            dt.items.add(
              truncateFileName(e.target.files[i], maxFileNameLength)
            );
          }
          e.target.files = dt.files;
        }
      } catch (err) {
        console.error("Error truncating file name:", err);
      }
    }
    if (handleFileChange) {
      handleFileChange(e);
    }
  };

  return (
    <div className={`w-100 ${className}`.trim()}>
      {label && (
        <label
          htmlFor={id || name}
          className={`form-label col-form-label w-100 fs-5 form-label-custom-visible p-0 ${labelClassName}`.trim()}
          style={labelStyle}
        >
          {label} {required && <span className="text-danger">*</span>}
        </label>
      )}
      <div className="w-100">
        <input
          ref={ref}
          className={`form-control fs-5 ${
            errorMessage && errorMessage?.[name]?.trim()?.length > 0
              ? "input-error"
              : ""
          }`}
          type="file"
          id={id || name}
          name={name}
          accept={acceptFormat}
          onChange={onInputChange}
          disabled={disabled}
          multiple={multiple}
          aria-label={ariaLabelText}
          aria-required={isRequired}
          aria-invalid={Boolean(errorMessage?.[name])}
          aria-describedby={
            errorMessage?.[name] ? `${id || name}-error` : undefined
          }
        />
      </div>
      {imageURL && (
        <div className="mt-2">
          <div
            className="position-relative d-inline-flex align-items-center justify-content-center bg-white border rounded p-1 flex-shrink-0"
            style={{ width: "70px", height: "70px" }}
          >
            <img
              src={imageURL}
              alt="Uploaded file preview"
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
              }}
            />
            {onClearImage && (
              <button
                type="button"
                className="btn btn-danger btn-sm p-0 d-flex align-items-center justify-content-center position-absolute top-0 end-0 shadow-sm"
                style={{
                  width: "20px",
                  height: "20px",
                  fontSize: "11px",
                  borderRadius: "50%",
                  transform: "translate(30%, -30%)",
                  lineHeight: 1,
                  zIndex: 2,
                }}
                onClick={onClearImage}
                title="Remove image"
                aria-label="Remove uploaded image"
              >
                ✕
              </button>
            )}
          </div>
        </div>
      )}
      {errorMessage &&
        errorMessage[name] &&
        Object?.keys(errorMessage).length > 0 && (
          <div
            id={`${id || name}-error`}
            role="alert"
            className="text-danger fw-700 fs-6 mt-1"
          >
            {errorMessage[name]}
          </div>
        )}
    </div>
  );
});

export default FileInput;
